import { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { fmtDate } from "@/lib/format";

// ---------------------------------------------------------------------------
// DateRangeFilter — Penz'in dört listeleme ekranı (İşler, Satın Almalar,
// Satış Faturaları, Alış Faturaları) için ortak tarih filtresi.
//
// Preset'ler kullanıcının yerel saatinin (browser TZ) takvim ayı/yılı
// üzerinden hesaplanır.  Backend'e her zaman ``YYYY-MM-DD`` biçiminde
// gönderilir; başlangıç ve bitiş DAHİLDİR (from ≤ x ≤ to).
//
// Parent yalnızca ``{from, to}`` alır.  Hem ikisi boş ise filtre aktif
// değildir → mevcut listeleme davranışı BOZULMAZ.
// ---------------------------------------------------------------------------

const PRESETS = [
  { key: "this_month",  label: "Bu Ay" },
  { key: "last_month",  label: "Geçen Ay" },
  { key: "this_year",   label: "Bu Yıl" },
  { key: "custom",      label: "Özel Tarih" },
];

function toIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function computePresetRange(key, now = new Date()) {
  if (key === "this_month") {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: toIso(from), to: toIso(to) };
  }
  if (key === "last_month") {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to   = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: toIso(from), to: toIso(to) };
  }
  if (key === "this_year") {
    const from = new Date(now.getFullYear(), 0, 1);
    return { from: toIso(from), to: toIso(now) };
  }
  return { from: "", to: "" };
}

export default function DateRangeFilter({ value, onChange, testId = "date-range" }) {
  // value: {from, to} or null → filtre aktif değil.
  const activeKey = useMemo(() => {
    if (!value?.from && !value?.to) return null;
    for (const p of PRESETS) {
      if (p.key === "custom") continue;
      const r = computePresetRange(p.key);
      if (r.from === value.from && r.to === value.to) return p.key;
    }
    return "custom";
  }, [value]);

  const [customOpen, setCustomOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(value?.from || "");
  const [draftTo,   setDraftTo]   = useState(value?.to   || "");

  useEffect(() => {
    if (customOpen) {
      setDraftFrom(value?.from || "");
      setDraftTo(value?.to || "");
    }
  }, [customOpen, value]);

  const selectPreset = (key) => {
    if (key === "custom") {
      setCustomOpen(true);
      return;
    }
    onChange?.(computePresetRange(key));
  };

  const applyCustom = () => {
    if (!draftFrom && !draftTo) {
      onChange?.({ from: "", to: "" });
      setCustomOpen(false);
      return;
    }
    if (draftFrom && draftTo && draftFrom > draftTo) {
      // basit koruma: başlangıç, bitişten büyük olamaz
      onChange?.({ from: draftTo, to: draftFrom });
    } else {
      onChange?.({ from: draftFrom, to: draftTo });
    }
    setCustomOpen(false);
  };

  const clear = () => onChange?.({ from: "", to: "" });

  const activeLabel = useMemo(() => {
    if (!activeKey) return null;
    if (activeKey === "custom") {
      if (value?.from && value?.to) return `${fmtDate(value.from)} → ${fmtDate(value.to)}`;
      if (value?.from) return `≥ ${fmtDate(value.from)}`;
      if (value?.to)   return `≤ ${fmtDate(value.to)}`;
    }
    return PRESETS.find((p) => p.key === activeKey)?.label;
  }, [activeKey, value]);

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testId}>
      {PRESETS.filter((p) => p.key !== "custom").map((p) => {
        const isActive = activeKey === p.key;
        return (
          <Button
            key={p.key}
            type="button"
            variant={isActive ? "default" : "outline"}
            size="sm"
            onClick={() => selectPreset(p.key)}
            className={`h-10 rounded-lg text-xs font-medium ${
              isActive ? "bg-slate-900 hover:bg-slate-800 text-white" : ""
            }`}
            data-testid={`${testId}-preset-${p.key}`}
          >
            {p.label}
          </Button>
        );
      })}

      {/* Custom aralık popover — "Özel Tarih" butonu doğrudan tetikleyicidir. */}
      <Popover open={customOpen} onOpenChange={setCustomOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant={activeKey === "custom" ? "default" : "outline"}
            size="sm"
            className={`h-10 rounded-lg text-xs font-medium ${
              activeKey === "custom" ? "bg-slate-900 hover:bg-slate-800 text-white" : ""
            }`}
            data-testid={`${testId}-preset-custom`}
          >
            <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
            Özel Tarih
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-4 space-y-3" data-testid={`${testId}-custom-popover`}>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Özel Tarih Aralığı</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Başlangıç</Label>
              <Input
                type="date"
                value={draftFrom}
                onChange={(e) => setDraftFrom(e.target.value)}
                className="h-10 rounded-lg"
                data-testid={`${testId}-custom-from`}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Bitiş</Label>
              <Input
                type="date"
                value={draftTo}
                onChange={(e) => setDraftTo(e.target.value)}
                className="h-10 rounded-lg"
                data-testid={`${testId}-custom-to`}
              />
            </div>
          </div>
          <div className="flex justify-between pt-1">
            <Button
              type="button" variant="ghost" size="sm"
              className="text-xs text-slate-500 h-8"
              onClick={() => { setDraftFrom(""); setDraftTo(""); }}
              data-testid={`${testId}-custom-reset`}
            >
              Sıfırla
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" className="h-8"
                onClick={() => setCustomOpen(false)}>
                Vazgeç
              </Button>
              <Button type="button" size="sm"
                className="h-8 bg-slate-900 hover:bg-slate-800"
                onClick={applyCustom}
                data-testid={`${testId}-custom-apply`}>
                Uygula
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {activeLabel && (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 text-xs font-medium ml-1"
          data-testid={`${testId}-active-chip`}
        >
          <CalendarIcon className="w-3.5 h-3.5" />
          <span>{activeLabel}</span>
          <button
            type="button"
            className="ml-0.5 hover:bg-amber-100 rounded p-0.5"
            onClick={clear}
            aria-label="Tarih filtresini temizle"
            data-testid={`${testId}-clear`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      )}
    </div>
  );
}

// Yardımcı: bir tarihin from/to (inclusive) aralığında olup olmadığını döner.
// ``date`` YYYY-MM-DD string ya da string ön ekli (ör. ISO datetime).
export function isDateInRange(date, from, to) {
  if (!from && !to) return true;
  if (!date) return false;
  const d = String(date).slice(0, 10);
  if (from && d < from) return false;
  if (to   && d > to)   return false;
  return true;
}
